import React from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../../data/projects';
import { SupportContent } from '../../data/projectContent';
import { projectNavigation, type ProjectNavigation } from './projectNavigation';
import './ProjectTemplates.css';

interface Props {
  project: Pick<Project, 'name' | 'slug' | 'links'>;
  content: SupportContent;
  navigation?: ProjectNavigation;
}

const ProjectSupportTemplate: React.FC<Props> = ({ project, content, navigation }) => {
  const { home, basePath } = projectNavigation(project, navigation);
  return (
    <div className="project-page project-support-page">
      <div className="project-page-container">
        <div className="project-page-header">
          <Link to={home.path} className="project-back-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to {home.label}
          </Link>
          <h1>{content.title ?? 'Support'}</h1>
          {content.intro && <p className="project-page-subtitle">{content.intro}</p>}
        </div>

        <div className="project-page-content">
          {/* Contact Section */}
          <section className="project-content-section">
            <h2>Contact Us</h2>
            <div className="project-contact-card">
              <div className="project-contact-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <div className="project-contact-details">
                <h3>Email Support</h3>
                <a href={`mailto:${content.contact.email}`} className="project-support-email">
                  {content.contact.email}
                </a>
                {content.contact.responseTime && <p>{content.contact.responseTime}</p>}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="project-content-section">
            <h2>Frequently Asked Questions</h2>
            {content.faqs.map((faq, index) => (
              <div key={index} className="project-faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </section>

          {/* Troubleshooting Section */}
          {content.troubleshooting && content.troubleshooting.length > 0 && (
            <section className="project-content-section">
              <h2>Troubleshooting</h2>
              {content.troubleshooting.map((item, index) => (
                <div key={index} className="project-troubleshoot-item">
                  <h3>{item.title}</h3>
                  <ol>
                    {item.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </section>
          )}

          {/* System Requirements */}
          {content.systemRequirements && content.systemRequirements.length > 0 && (
            <section className="project-content-section">
              <h2>System Requirements</h2>
              <ul className="project-requirements-list">
                {content.systemRequirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Quick Links */}
          <section className="project-content-section project-quick-links-section">
            <h2>Quick Links</h2>
            <div className="project-quick-links">
              {project.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  className="project-quick-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ))}
              <Link to={`${basePath}/privacy`} className="project-quick-link">
                Privacy Policy
              </Link>
              {navigation?.termsPath && (
                <Link to={navigation.termsPath} className="project-quick-link">Terms of Service</Link>
              )}
            </div>
          </section>

          <div className="project-page-footer">
            <p>&copy; {new Date().getFullYear()} {project.name} by Apres AI LLC. All rights reserved.</p>
            <p className="project-footer-links">
              <Link to={home.path}>{home.label} Home</Link>
              {' · '}
              <Link to={`${basePath}/privacy`}>Privacy</Link>
              {' · '}
              <Link to="/">Apres AI</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectSupportTemplate;
